import {Product} from '#apollo/schema';
import {CTA_DISPLAY_NAME} from '#constant/products';
import {Colors} from '#theme';
import {formattedDate} from '#util';
import {SubscriptionStatusEnum} from '#util/subscriptions';

import {
  GetValuesArg,
  GetValuesReturn,
  SubscriptionStatus,
  SubscriptionTabType,
} from './CTASubscription.types';

import * as css from './CTASubscription.css';
import {themeClass} from './CTASubscription.css';

export const getValues = ({
  status,
  earliestBillingDateMemFormatted,
  earliestBillingDateSubFormatted,
  subscription,
  hasMembership,
  canceledOrExpired,
  membershipProductVariantIds,
  prevOrderDatePlus30,
  prevOrderDatePlus90,
  productVariants,
  allSupplementProducts,
  activeSupplementProducts,
}: GetValuesArg): GetValuesReturn => {
  const hasMembershipInSubscription = subscription?.items.some(item =>
    membershipProductVariantIds.includes(String(item.variantId)),
  );
  const wasMember = hasMembershipInSubscription && status === SubscriptionStatusEnum.CANCELLED;
  const wasSupplementSubscriber =
    (subscription?.items?.length ?? 0) > 0 &&
    !hasMembershipInSubscription &&
    status === SubscriptionStatusEnum.CANCELLED;
  const paused = status?.toUpperCase() === SubscriptionStatusEnum.PAUSED;
  let displayStatus = (status?.toUpperCase() || '') as SubscriptionStatus;

  const supplementsDisplayNames: string[] = [];
  type ProductTid = keyof typeof CTA_DISPLAY_NAME;

  allSupplementProducts.forEach(product => {
    if (product.tid in CTA_DISPLAY_NAME) {
      supplementsDisplayNames.push(CTA_DISPLAY_NAME[product.tid as ProductTid]);
    }
  });

  // Sort by length (longest to shortest), then alphabetically
  supplementsDisplayNames.sort((a, b) => {
    if (a.length !== b.length) {
      return b.length - a.length;
    }
    return a.localeCompare(b);
  });

  if (allSupplementProducts.length == 1) {
    const supplementNameWithOnly = supplementsDisplayNames[0] + ' Only';
    supplementsDisplayNames[0] = supplementNameWithOnly;
  }

  // Customers with a membership
  if ((hasMembership && !!subscription) || wasMember) {
    // Guard against an unresolved date so we never render "Member since null"
    let displayText = earliestBillingDateMemFormatted
      ? `🚀 Member since ${earliestBillingDateMemFormatted}`
      : `🚀 Member`;

    // Check for paused or cancelled memberships
    if (paused || canceledOrExpired) {
      if (prevOrderDatePlus30 > new Date()) {
        displayStatus = 'ACTIVE';
        displayText = `Your Membership is scheduled to ${
          paused ? 'pause' : 'end'
        } on ${formattedDate(prevOrderDatePlus30)}`;
      }
    }

    const {pendingDowngrade, textOutput} = subscription
      ? isPendingMembershipDowngrade(
          subscription,
          hasMembership,
          membershipProductVariantIds,
          activeSupplementProducts,
          prevOrderDatePlus30,
        )
      : {pendingDowngrade: false, textOutput: ''};
    if (pendingDowngrade) {
      displayText = textOutput;
    }

    const bgImage = `/image/profile/bg/bg-generic.png`;
    const bgImageMobile = `/image/profile/bg/bg-generic-mobile.png`;

    const textColor = 'white' as Colors;
    const textSinceColor = 'white' as Colors;
    const skipBtnVariant = 'primary-dark';

    return getMembersDisplay(
      displayStatus,
      displayText,
      supplementsDisplayNames,
      bgImage,
      bgImageMobile,
      textColor,
      textSinceColor,
      skipBtnVariant,
    );
  }
  // Non-members with a supplement subscription
  else if ((!hasMembership && !!subscription && !canceledOrExpired) || wasSupplementSubscriber) {
    // getting the non-member test kit variant for supplement subscribers
    const testKitVariant = productVariants.filter(pv => pv.tid === 'pv-tk-oneoff')[0]?.id || null;

    let textSince = earliestBillingDateSubFormatted
      ? `🚀 Subscriber since ${earliestBillingDateSubFormatted}`
      : `🚀 Subscriber`;

    const textColor = 'white' as Colors;
    const textSinceColor = 'white' as Colors;
    const skipBtnVariant = 'primary-dark';

    const bgImage = `/image/profile/bg/bg-generic.png`;
    const bgImageMobile = `/image/profile/bg/bg-generic-mobile.png`;

    return getSupplementsDisplay(
      displayStatus,
      textSince,
      supplementsDisplayNames,
      bgImage,
      bgImageMobile,
      testKitVariant || '',
      textColor,
      textSinceColor,
      skipBtnVariant,
    );
  }

  const bgImage = `image/profile/upsell-upgrade-all-products.png`;
  const bgImageTablet = `image/profile/upsell-upgrade-all-products-wide.png`;
  const bgImageMobile = `image/profile/upsell-upgrade-all-products-mobile.png`;

  return getNonMemNonSubsDisplay(bgImage, bgImageTablet, bgImageMobile);
};

export const getMembersDisplay = (
  displayStatus: SubscriptionStatus,
  displayText: string,
  supplementsDisplayNames: string[],
  bgImage: string,
  bgImageMobile: string,
  textColor: Colors,
  textSinceColor: Colors,
  skipBtnVariant: 'primary-dark' | 'secondary' | undefined,
  overrides: Record<string, string> = {},
): GetValuesReturn => {
  return {
    displayStatus,
    heading: 'Membership',
    subHeading: supplementsDisplayNames,
    bgMain: css.bgMemberMain,
    bgImage,
    bgImageMobile,
    textColor,
    textColorSubscriptionStatus: 'white' as Colors,
    textSince: displayText,
    textSinceColor,
    headingSecond: 'Give $30, Get $30',
    subheadingSecond: 'Share with friends and earn rewards',
    textColorSecond: 'white' as Colors,
    textColorSubHeadingSecond: 'white' as Colors,
    bgUpSell: css.bgCTAMembership,
    secondBtnVariant: 'secondary',
    secondBtnText: 'Refer a Friend',
    secondBtnLink: '/shop/pages/referrals',
    skipBtnVariant: skipBtnVariant,
    align: 'bottom',
    ...overrides,
  };
};

export const getSupplementsDisplay = (
  displayStatus: SubscriptionStatus,
  textSince: string,
  supplementsDisplayNames: string[],
  bgImage: string,
  bgImageMobile: string,
  testKitVariant: string,
  textColor: Colors,
  textSinceColor: Colors,
  skipBtnVariant: 'primary-dark' | 'secondary' | undefined,
  overrides: Record<string, string> = {},
): GetValuesReturn => {
  return {
    displayStatus,
    textColorSubscriptionStatus: 'white',
    heading: 'Supplements',
    subHeading: supplementsDisplayNames,
    bgImage,
    bgImageMobile,
    bgMain: themeClass,
    textColor: textColor,
    textSince,
    textSinceColor: textSinceColor,
    headingSecond: 'Purchase a Test',
    subheadingSecond: 'Discover your epigenetic age',
    textColorSecond: 'white',
    textColorSubHeadingSecond: 'white',
    bgUpSell: css.bgCTASupplements,
    secondBtnVariant: 'secondary',
    secondBtnText: 'Buy Now',
    secondBtnLink: `/shop/cart/${testKitVariant}:1`,
    skipBtnVariant: skipBtnVariant,
    ...overrides,
  };
};

// Non members without a subscription - individual testers
export const getNonMemNonSubsDisplay = (
  bgImage: string,
  bgImageTablet: string,
  bgImageMobile: string,
  overrides: Record<string, string> = {},
): GetValuesReturn => {
  return {
    displayStatus: '',
    textColorSubscriptionStatus: 'white',
    bgMain: css.bgCTAIndividualTester,
    bgTablet: css.bgCTAIndividualTesterTablet,
    bgMobile: css.bgCTAIndividualTesterMobile,
    heading: 'The future of healthy aging',
    subHeading: [
      'Take control of how you age. Unlock healthier years with science-backed tools designed to improve your healthspan and longevity.',
    ],
    subHeadingMobile: ['Start your longevity journey.'],
    textColor: 'white',
    textSinceColor: 'white',
    textSince: '',
    bgImage,
    bgImageTablet,
    bgImageMobile,
    ...overrides,
  };
};

export const isPendingMembershipDowngrade = (
  subscription: SubscriptionTabType,
  hasMembership: boolean,
  membershipProductVariantIds: string[] = [],
  activeSupplementProducts: Product[] = [],
  prevPlus30Date: Date,
) => {
  // Check if the account is pending a membership downgrade
  const hasMembershipUpcoming = subscription.items.some(item =>
    membershipProductVariantIds.includes(String(item.variantId)),
  );
  const pendingDowngrade = hasMembership && !hasMembershipUpcoming;

  if (!pendingDowngrade) {
    return {
      pendingDowngrade,
      textOutput: '',
    };
  }

  const supplementsDisplayNames: string[] = [];
  type ProductTid = keyof typeof CTA_DISPLAY_NAME;

  activeSupplementProducts.forEach(product => {
    if (product.tid in CTA_DISPLAY_NAME) {
      supplementsDisplayNames.push(CTA_DISPLAY_NAME[product.tid as ProductTid]);
    }
  });

  const supplementDisplayNamesFlattened = supplementsDisplayNames
    .flatMap(item => item.split(' + '))
    .sort((a, b) => {
      const lengthDifference = b.length - a.length;
      if (lengthDifference !== 0) {
        return lengthDifference;
      } else {
        return a.localeCompare(b);
      }
    });

  let textOutput = '';
  if (supplementDisplayNamesFlattened.length == 1) {
    textOutput = `Your Membership will change to a ${supplementDisplayNamesFlattened[0]}
       Only subscription on ${formattedDate(prevPlus30Date)}`;
  } else if (supplementDisplayNamesFlattened.length > 1) {
    const supplementsString = supplementDisplayNamesFlattened.join(' + ');
    textOutput = `Your Membership will change to a ${supplementsString} subscription on ${formattedDate(
      prevPlus30Date,
    )}`;
  } else {
    textOutput = `Your Membership will end on ${formattedDate(prevPlus30Date)}`;
  }

  return {
    pendingDowngrade,
    textOutput,
  };
};
